<template>
  <div v-if="photo" class="p-relative h-100-percentage d-flex align-center">
    <v-zoomer
      ref="zoomer"
      :zoomed.sync="zoomed"
      :limit-translation="false"
      :max-scale="6"
      :mouse-wheel-to-zoom="zoomed"
      style="width: 100%; height: 100%;"
    >
      <v-img
        :key="photo.id"
        :src="$asa.getImageUrl(photo, zoomed ? 'zoom' : 'full')"
        :lazy-src="$asa.getImageUrl(photo, zoomed ? 'full' : 'thumb')"
        :alt="`Photo by ${photo.photographerName}`"
        :draggable="false"
        :contain="true"
        :max-height="'90vh'"
        @click.right.stop.prevent="onRightClick"
        @loadstart="zoomStartLoading"
        @load="zoomLoaded"
      >
        <template #placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular
              indeterminate
              color="tertiary"
            ></v-progress-circular>
          </v-row>
        </template>
        <div v-if="zoomLoading" class="h-100-percentage d-flex align-center justify-center">
          <v-icon small color="white">fal fa-spinner fa-spin</v-icon>
        </div>
      </v-img>
    </v-zoomer>
    <v-hover v-if="!zoomed">
      <div
        slot-scope="{ hover }"
        class="top-actions-mask mask-right"
        @click="toggleExpand"
      >
        <v-btn
          :input-value="hover"
          :class="{ transparent: !hover, 'hide-action-btn': mouseStopped }"
          class="my-2 mr-2 fullscreen-btn"
          dark
          absolute
          fab
          small
          depressed
        >
          <v-icon dark small>fal fa-expand-arrows</v-icon>
        </v-btn>
      </div>
    </v-hover>
    <v-hover v-if="!zoomed && sorround.previousPhotoId && full">
      <div
        slot-scope="{ hover }"
        class="navigation-mask mask-left"
        @click.right.stop.prevent="onRightClick"
        @click="setPhotoFromSorround(sorround.previousPhotoId)"
      >
        <v-btn
          :input-value="hover"
          :class="{ transparent: !hover, 'hide-action-btn': mouseStopped }"
          dark
          class="ml-2 prev-btn"
          small
          absolute
          fab
          depressed
          nuxt
        >
          <v-icon small dark class="d-inline-flex">fal fa-angle-left</v-icon>
        </v-btn>
      </div>
    </v-hover>
    <v-hover v-if="!zoomed && sorround.nextPhotoId && full">
      <div
        slot-scope="{ hover }"
        class="navigation-mask mask-right"
        @click.right.stop.prevent="onRightClick"
        @click="setPhotoFromSorround(sorround.nextPhotoId)"
      >
        <v-btn
          :input-value="hover"
          :class="{ transparent: !hover, 'hide-action-btn': mouseStopped }"
          dark
          class="mr-2 next-btn"
          absolute
          fab
          small
          depressed
          nuxt
        >
          <v-icon small dark class="d-inline-flex">fal fa-angle-right</v-icon>
        </v-btn>
      </div>
    </v-hover>
    <v-hover>
      <div
        slot-scope="{ hover }"
        class="bottom-actions-mask mask-right"
        @click="toggleZoomer"
      >
        <v-btn
          :input-value="hover"
          :class="{ transparent: !hover, 'hide-action-btn': mouseStopped }"
          dark
          class="my-2 mr-2 zoom-btn"
          absolute
          fab
          small
          depressed
          nuxt
        >
          <v-icon small dark class="d-inline-flex"
            >fal fa-search-{{ zoomed ? 'minus' : 'plus' }}</v-icon
          >
        </v-btn>
      </div>
    </v-hover>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import types from '@/utils/types'
import { EventBus } from '@/plugins/event-bus'

export default {
  name: 'CurationDialogPhoto',
  props: {
    photo: {
      type: Object,
      default: () => {}
    },
    full: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      zoomed: false,
      mouseStopped: true,
      delay: setInterval(this.delayCheck, 500),
      timedelay: 1,
      allowKeyboardNavigation: true,
      zoomLoading: false
    }
  },
  computed: {
    ...mapState({
      sorround: state => state.account.sorround
    })
  },
  mounted() {
    EventBus.$on(
      'set-allow-keyboard-navigation',
      value => (this.allowKeyboardNavigation = value)
    )
    document.addEventListener('mousemove', this.showActionBtns)
    if (this.full) {
      document.addEventListener('keydown', this.handleKeyboardSorround)
    }
    const el = document.getElementsByClassName('zoomer')[0]
    el.className += ' d-flex'
  },
  beforeDestroy() {
    this.$refs.zoomer.reset()
    document.removeEventListener('keydown', this.handleKeyboardSorround)
  },
  methods: {
    handleKeyboardSorround(e) {
      if (this.allowKeyboardNavigation) {
        if (e.keyCode === 39 && this.sorround.nextPhotoId) {
          this.setPhotoFromSorround(this.sorround.nextPhotoId)
        }
        if (e.keyCode === 37 && this.sorround.previousPhotoId) {
          this.setPhotoFromSorround(this.sorround.previousPhotoId)
        }
      }
    },
    onRightClick() {
      let text = 'This photo is © of '
      text +=
        this.photo.statusName === types.photo.status.inCuration
          ? 'the photographer'
          : this.photo.photographerName
      EventBus.$emit('snackbar', {
        text,
        color: 'info'
      })
    },
    showActionBtns() {
      this.mouseStopped = false
      this.timedelay = 1
      clearInterval(this.delay)
      this.delay = setInterval(this.delayCheck, 500)
    },
    delayCheck() {
      if (this.timedelay === 2) {
        this.mouseStopped = true
        this.timedelay = 1
      }
      this.timedelay = this.timedelay + 1
    },
    toggleExpand() {
      EventBus.$emit('toggle-photo-expand-dialog')
    },
    setPhotoFromSorround(id) {
      EventBus.$emit('set-curation-photo-from-sorround', id)
    },
    toggleZoomer() {
      this.zoomed ? this.$refs.zoomer.reset() : this.$refs.zoomer.zoomIn()
    },
    zoomStartLoading() {
      if (this.zoomed) {
        this.zoomLoading = true
      }
    },
    zoomLoaded() {
      if (this.zoomed) {
        this.zoomLoading = false
      }
    }
  }
}
</script>

<style scoped>
.fullscreen-btn {
  top: 0;
  right: 0;
}
.prev-btn {
  left: 0;
}
.next-btn {
  right: 0;
}
.zoom-btn {
  right: 0;
}
.top-actions-mask {
  align-items: center;
  cursor: pointer;
  display: flex;
  position: absolute;
  top: 0;
  width: 50%;
  height: 60px;
}
.bottom-actions-mask {
  align-items: center;
  cursor: pointer;
  display: flex;
  position: absolute;
  bottom: 0;
  width: 50%;
  height: 60px;
}
.bottom-actions-mask.mask-right {
  right: 0;
}
.top-actions-mask.mask-right {
  right: 0;
}
.navigation-mask {
  align-items: center;
  cursor: pointer;
  display: flex;
  position: absolute;
  top: 60px;
  width: 20%;
  height: calc(100% - 120px);
}
.navigation-mask.mask-left {
  left: 0;
}
.navigation-mask.mask-right {
  right: 0;
}
.hide-action-btn {
  opacity: 0;
}
</style>
